import React, { useMemo } from 'react';
import Map, {
    Source,
    Layer,
    Marker,
    FullscreenControl,
    MapLayerMouseEvent
} from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MAPBOX_ACCESS_TOKEN } from '../../../../core/map';
import {
    calculateBoundingBox,
    calculateCenter,
    calculateZoomLevel
} from '../../flags-tool/utils/GpxMap.utils';
import { EnrichedTrack, SelectedWorkout } from '../types';

interface TrackMapProps {
    tracks: EnrichedTrack[];
    selectedWorkouts: SelectedWorkout[];
    progress: number;
    focusedTrackId: number | null;
    onMapClick: (workoutId: number, pointIdx: number) => void;
}

const currentPoint = (track: EnrichedTrack, progress: number) => {
    const n = track.points.length;
    if (n === 0) return null;
    const idx = Math.min(n - 1, Math.max(0, Math.floor(progress * (n - 1))));
    return track.points[idx];
};

const findNearestPoint = (
    tracks: EnrichedTrack[],
    lat: number,
    lng: number
): { workoutId: number; idx: number } | null => {
    let best: { workoutId: number; idx: number; d2: number } | null = null;
    for (const track of tracks) {
        for (let i = 0; i < track.points.length; i++) {
            const p = track.points[i];
            const dx = p.lat - lat;
            const dy = p.lon - lng;
            const d2 = dx * dx + dy * dy;
            if (!best || d2 < best.d2) {
                best = { workoutId: track.workoutId, idx: i, d2 };
            }
        }
    }
    return best ? { workoutId: best.workoutId, idx: best.idx } : null;
};

export function TrackMap({
    tracks,
    selectedWorkouts,
    progress,
    focusedTrackId,
    onMapClick
}: TrackMapProps) {
    const initialViewState = useMemo(() => {
        const allPoints: [number, number][] = [];
        tracks.forEach((t) =>
            t.points.forEach((p) => allPoints.push([p.lon, p.lat]))
        );
        const bbox = calculateBoundingBox(allPoints);
        if (!bbox) {
            return { longitude: 0, latitude: 0, zoom: 2 };
        }
        const center = calculateCenter(bbox);
        const zoom = calculateZoomLevel({
            boundingBox: bbox,
            containerWidth: 800,
            containerHeight: 400
        });
        return { longitude: center[0], latitude: center[1], zoom };
        // re-fit only when track ids change, not on every progress tick
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tracks.map((t) => t.workoutId).join(',')]);

    const handleClick = (e: MapLayerMouseEvent) => {
        const hit = findNearestPoint(tracks, e.lngLat.lat, e.lngLat.lng);
        if (hit) onMapClick(hit.workoutId, hit.idx);
    };

    return (
        <div className="h-[420px] overflow-hidden rounded-lg border">
            <Map
                initialViewState={initialViewState}
                style={{ width: '100%', height: '100%' }}
                mapStyle="mapbox://styles/mapbox/dark-v11"
                mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
                onClick={handleClick}
            >
                {tracks.map((track) => {
                    const sw = selectedWorkouts.find(
                        (w) => w.id === track.workoutId
                    );
                    const color = sw?.color ?? '#38bdf8';
                    const isFocused = focusedTrackId === track.workoutId;
                    const coords = track.points.map(
                        (p) => [p.lon, p.lat] as [number, number]
                    );
                    return (
                        <Source
                            key={track.workoutId}
                            id={`gpx-source-${track.workoutId}`}
                            type="geojson"
                            data={{
                                type: 'Feature',
                                properties: {},
                                geometry: {
                                    type: 'LineString',
                                    coordinates: coords
                                }
                            }}
                        >
                            <Layer
                                id={`gpx-layer-${track.workoutId}`}
                                type="line"
                                layout={{
                                    'line-join': 'round',
                                    'line-cap': 'round'
                                }}
                                paint={{
                                    'line-color': color,
                                    'line-width': isFocused ? 5 : 3,
                                    'line-opacity': isFocused ? 1 : 0.7
                                }}
                            />
                        </Source>
                    );
                })}

                {tracks.map((track) => {
                    const sw = selectedWorkouts.find(
                        (w) => w.id === track.workoutId
                    );
                    const color = sw?.color ?? '#f472b6';
                    const point = currentPoint(track, progress);
                    if (!point) return null;
                    return (
                        <Marker
                            key={`marker-${track.workoutId}`}
                            longitude={point.lon}
                            latitude={point.lat}
                            anchor="center"
                        >
                            <div
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onMapClick(track.workoutId, point.idx);
                                }}
                                className="size-3.5 cursor-pointer rounded-full border-2 border-white shadow-[0_0_6px_rgba(0,0,0,0.5)]"
                                style={{ backgroundColor: color }}
                            />
                        </Marker>
                    );
                })}

                <FullscreenControl position="top-left" />
            </Map>
        </div>
    );
}
